import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { EmailPreview } from "@/components/admin/EmailPreview";
import { adminContent } from "@/i18n/admin";
import { useLang, type Lang } from "@/i18n/language";
import { supabase } from "@/integrations/supabase/client";
import { renderTemplate, type EmailTemplateRow } from "@/lib/emailTemplate";

const inputClass =
  "w-full rounded-lg border border-border bg-background/60 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary";
const cardClass = "rounded-xl border border-border bg-surface p-6";
const btnPrimary =
  "rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50";
const btnGhost =
  "rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50";

export function useEmailTemplate() {
  return useQuery({
    queryKey: ["email-template", "session_invite"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .eq("slug", "session_invite")
        .maybeSingle();
      if (error) throw error;
      return (data as EmailTemplateRow | null) ?? null;
    },
  });
}

const FIELDS = [
  ["name_es", "nameEs", false],
  ["name_en", "nameEn", false],
  ["subject_es", "subjectEs", false],
  ["subject_en", "subjectEn", false],
  ["body_es", "bodyEs", true],
  ["body_en", "bodyEn", true],
  ["button_label_es", "buttonEs", false],
  ["button_label_en", "buttonEn", false],
  ["signature_es", "signatureEs", false],
  ["signature_en", "signatureEn", false],
] as const;

export function EmailTemplateEditor() {
  const { lang } = useLang();
  const t = adminContent[lang].template;
  const queryClient = useQueryClient();
  const templateQuery = useEmailTemplate();
  const [draft, setDraft] = useState<EmailTemplateRow | null>(null);
  const [previewLang, setPreviewLang] = useState<Lang>(lang);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (templateQuery.data) setDraft(templateQuery.data);
  }, [templateQuery.data]);

  if (!draft) {
    return (
      <p className="text-sm text-muted-foreground">
        {templateQuery.isLoading ? "…" : t.empty}
      </p>
    );
  }

  function set<K extends keyof EmailTemplateRow>(key: K, value: EmailTemplateRow[K]) {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    const { error } = await supabase
      .from("email_templates")
      .update({
        name_es: draft.name_es,
        name_en: draft.name_en,
        subject_es: draft.subject_es,
        subject_en: draft.subject_en,
        body_es: draft.body_es,
        body_en: draft.body_en,
        button_label_es: draft.button_label_es,
        button_label_en: draft.button_label_en,
        signature_es: draft.signature_es,
        signature_en: draft.signature_en,
        header_image_url: draft.header_image_url,
      })
      .eq("id", draft.id);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t.saved);
    queryClient.invalidateQueries({ queryKey: ["email-template", "session_invite"] });
  }

  const preview = renderTemplate(draft, previewLang, {
    nombre: previewLang === "es" ? "Dra. Ana Ruiz" : "Dr. Ana Ruiz",
    grupo: previewLang === "es" ? "Grupo Básico Otoño 2026" : "Basic Group Fall 2026",
    fecha: previewLang === "es" ? "12 de octubre de 2026" : "October 12, 2026",
    enlace: "https://meet.google.com/stm-demo",
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
      <section className={cardClass}>
        <h3 className="text-base font-semibold">{t.heading}</h3>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t.lead}</p>

        <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-primary">
            {t.variables}
          </p>
          <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
            {t.variablesHint}
          </p>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {FIELDS.map(([key, labelKey, multiline]) => (
            <div key={key} className={multiline ? "md:col-span-2" : undefined}>
              <label className="block text-xs font-semibold text-muted-foreground">
                {t[labelKey]}
              </label>
              {multiline ? (
                <textarea
                  value={draft[key] ?? ""}
                  onChange={(e) => set(key, e.target.value)}
                  rows={8}
                  className={`${inputClass} mt-1.5 resize-y`}
                />
              ) : (
                <input
                  value={draft[key] ?? ""}
                  onChange={(e) => set(key, e.target.value)}
                  className={`${inputClass} mt-1.5`}
                />
              )}
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-muted-foreground">{t.image}</label>
            <input
              value={draft.header_image_url ?? ""}
              onChange={(e) => set("header_image_url", e.target.value)}
              className={`${inputClass} mt-1.5`}
            />
            <p className="mt-1.5 text-[11px] text-muted-foreground">{t.imageHint}</p>
          </div>
        </div>

        <button onClick={save} disabled={busy} className={`${btnPrimary} mt-5`}>
          {t.save}
        </button>
      </section>

      <section className="h-fit">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
            {t.preview}
          </h3>
          <div className="flex gap-2">
            {(["es", "en"] as const).map((key) => (
              <button
                key={key}
                onClick={() => setPreviewLang(key)}
                className={previewLang === key ? btnPrimary : btnGhost}
              >
                {key.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3">
          <EmailPreview
            subject={preview.subject}
            body={preview.body}
            buttonLabel={preview.buttonLabel}
            signature={preview.signature}
            headerImage={preview.headerImage}
            link="https://meet.google.com/stm-demo"
          />
        </div>
      </section>
    </div>
  );
}
